import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';

export function SignaturePad({ onSignatureChange, label, testId }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [typedName, setTypedName] = useState('');
  const [mode, setMode] = useState('draw'); // 'draw' or 'type'

  // Size the canvas backing store to its on-screen size so pointer coordinates
  // map 1:1. Must re-run whenever the canvas (re)mounts after a mode switch, or
  // when the window resizes — otherwise the stroke lands offset from the pointer.
  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const ratio = window.devicePixelRatio || 2;
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(ratio, ratio);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    setHasSignature(false);
    onSignatureChange?.('');
  }, [onSignatureChange]);

  useEffect(() => {
    if (mode !== 'draw') return;
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, [mode, resizeCanvas]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    return {
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDraw = () => {
    if (isDrawing) {
      setIsDrawing(false);
      if (hasSignature) {
        onSignatureChange?.(canvasRef.current.toDataURL('image/png'));
      }
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onSignatureChange?.('');
  };

  const handleTypedNameChange = (e) => {
    setTypedName(e.target.value);
    onSignatureChange?.(e.target.value ? `typed:${e.target.value}` : '');
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">{label}</label>
        <div className="flex gap-1">
          <button
            type="button"
            className={`text-xs px-2 py-1 rounded ${mode === 'draw' ? 'bg-brand text-brand-contrast' : 'bg-slate-100 text-slate-600'}`}
            onClick={() => setMode('draw')}
          >Draw</button>
          <button
            type="button"
            className={`text-xs px-2 py-1 rounded ${mode === 'type' ? 'bg-brand text-brand-contrast' : 'bg-slate-100 text-slate-600'}`}
            onClick={() => setMode('type')}
          >Type</button>
        </div>
      </div>
      {mode === 'draw' ? (
        <div className="relative">
          <canvas
            ref={canvasRef}
            className="w-full h-24 border-2 border-dashed border-slate-300 rounded-lg bg-white cursor-crosshair touch-none"
            onMouseDown={startDraw}
            onMouseMove={draw}
            onMouseUp={stopDraw}
            onMouseLeave={stopDraw}
            onTouchStart={startDraw}
            onTouchMove={draw}
            onTouchEnd={stopDraw}
          />
          {!hasSignature && (
            <p className="absolute inset-0 flex items-center justify-center text-sm text-slate-400 pointer-events-none">
              Sign here
            </p>
          )}
          {hasSignature && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute top-1 right-1 text-xs h-6"
              onClick={clearSignature}
            >Clear</Button>
          )}
        </div>
      ) : (
        <div className="relative">
          <input
            type="text"
            value={typedName}
            onChange={handleTypedNameChange}
            placeholder="Type full name"
            className="w-full h-12 border-2 border-dashed border-slate-300 rounded-lg bg-white px-4 text-lg italic font-serif text-black caret-black placeholder:text-slate-400"
            data-testid={`${testId}-typed-input`}
          />
        </div>
      )}
    </div>
  );
}
